import { memo, useCallback, useMemo, useState } from 'react';

import { merge } from '@/components';
import { Button, Flex, Heading, Text } from '@/components/core';
import { CloseIcon, SaveIcon } from '@/components/icons';
import { toAgentRunId, useDeleteAgentRun } from '@/hooks/api';
import { DivProps } from '@/utils';

export type DeleteAgentRunsProps = Omit<DivProps, 'children'> & {
  agentId: string;
  runIds: string[];
  close?: () => void;
};

const IDeleteAgentRuns = ({ agentId, runIds, close, className, ...rest }: DeleteAgentRunsProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { mutateAsync: deleteAgentRun } = useDeleteAgentRun();

  const onDeleteAgentRun = useCallback(async () => {
    setIsDeleting(true);
    for (const runId of runIds) {
      await deleteAgentRun(toAgentRunId(agentId, runId));
    }
    setIsDeleting(false);
    close?.();
  }, [agentId, close, deleteAgentRun, runIds]);

  const { heading, descriptionPart } = useMemo(() => {
    if (runIds.length === 1) {
      return { heading: 'Delete run?', descriptionPart: 'this run' };
    }
    return { heading: `Delete runs?`, descriptionPart: `${runIds.length} runs` };
  }, [runIds]);

  return (
    <Flex col className={merge('gap-md', className)} {...rest}>
      <Flex className="items-start">
        <Flex col className="gap-xs">
          <Heading>{heading}</Heading>
          <Text>
            Are you sure you want to delete {descriptionPart}?<br />
            This action cannot be undone.
          </Text>
        </Flex>
        <div className="grow" />
        <Button onPress={close} variant="plain">
          <CloseIcon />
        </Button>
      </Flex>
      <Flex className="gap-sm justify-end">
        <Button onPress={close} variant="secondary" isDisabled={isDeleting}>
          <Text>Cancel</Text>
        </Button>
        <Button onPress={onDeleteAgentRun} variant="danger" isDisabled={isDeleting}>
          {isDeleting ? <SaveIcon /> : null}
          <Text>Delete</Text>
        </Button>
      </Flex>
    </Flex>
  );
};

export const DeleteAgentRuns = memo(IDeleteAgentRuns) as typeof IDeleteAgentRuns;
